import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { LogOut, User } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { ProfileAvatar } from "@/shared/ui/ProfileAvatar";
import {
  clearSession,
  getAccountDisplayLabel,
  getRoleDisplayLabel,
  getStoredAccountType,
  AccountType,
} from "@/modules/safecheck/auth";
import { logoutRequest } from "@/modules/safecheck/api/companyApi";
import { getAccountAvatarInitials, enterpriseBrand } from "@/modules/safecheck/branding";
import { cn } from "@/shared/lib/cn";

const roleLabels: Record<AccountType, string> = {
  admin: "ADMIN",
  auditor: "AUDITEUR",
  hospital: "HOPITAL",
  enterprise: "ENTREPRISE",
};

function resolveRoleLabel(accountType: AccountType | null): string {
  const display = getRoleDisplayLabel();
  if (display === "SUPER ADMIN") return display;
  return accountType ? roleLabels[accountType] : "ADMIN";
}

export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAccountType(getStoredAccountType());
    setUserName(getAccountDisplayLabel());
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const accountLabel =
    userName ??
    (accountType === "admin"
      ? t.header.userNames.admin
      : accountType === "auditor"
        ? t.header.userNames.auditor
        : accountType === "enterprise"
          ? enterpriseBrand.alt
          : accountType
            ? t.safecheck.accountType.types[accountType].title
            : t.header.profile);
  const roleLabel = resolveRoleLabel(accountType);

  const handleOpenProfile = () => {
    setIsOpen(false);
    navigate("/dashboard/settings/user");
  };

  const handleLogout = async () => {
    setIsOpen(false);
    try {
      await logoutRequest();
    } catch {
      // Token expiré ou session déjà fermée — on nettoie quand même.
    }
    clearSession();
    navigate("/login");
  };

  return (
    <div ref={menuRef} className="relative flex items-center gap-2 border-l border-border pl-2 sm:gap-3 sm:pl-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t.header.profile}
        aria-expanded={isOpen}
        className="flex items-center gap-3 text-left"
      >
        <div className="hidden text-right sm:block">
          <p className="text-sm font-bold text-foreground">{accountLabel}</p>
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted">
            {roleLabel}
          </p>
        </div>
        <div
          className={cn(
            "relative h-9 w-9 shrink-0 overflow-hidden rounded-full ring-2 ring-primary/20 sm:h-11 sm:w-11",
            isOpen && "ring-primary/50",
          )}
        >
          <ProfileAvatar
            initials={getAccountAvatarInitials(accountType, accountLabel)}
            size={44}
            className="h-full w-full"
          />
        </div>
      </button>

      {isOpen && (
        <div className="card-shadow absolute right-0 top-full z-50 mt-3 w-[min(14rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-card sm:w-56">
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-semibold text-foreground">{accountLabel}</p>
            <p className="text-xs text-muted">{t.header.sessionActive}</p>
          </div>

          <div className="p-1.5">
            <button
              type="button"
              onClick={handleOpenProfile}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-card-muted"
            >
              <User size={16} className="text-primary" />
              {t.header.profile}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-pink transition-colors hover:bg-pink-soft"
            >
              <LogOut size={16} />
              {t.header.logout}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
